/**
 * Back office → Catalogue → package: price, instalments linked to the file steps, inclusions,
 * exclusions, conditions, values "to complete" (D-069), publication and published versions.
 */

import {
  type PackageItemKindEnum as KindEnum,
  type PricingEnum,
  type Service,
  type ServicePackage,
  type ServicePackageRequest,
  getCatalogPackagesListQueryKey,
  getCatalogPackagesRetrieveQueryKey,
  useCatalogPackagesArchiveCreate,
  useCatalogPackagesCreate,
  useCatalogPackagesPartialUpdate,
  useCatalogPackagesPublishCreate,
  useCatalogPackagesRetrieve,
  useCatalogPackagesVersionsList,
  useCatalogServicesRetrieve,
} from "@agencyos/api-client";
import { formatXaf, installmentsTotal, readAmount } from "@agencyos/core";
import {
  Badge,
  Button,
  GlassCard,
  SelectField,
  Skeleton,
  SoftToggle,
  TextField,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Page } from "../components/AppShell";
import { type Texts, TranslatedFields, asTexts, localized } from "../components/TranslatedFields";
import { fieldErrors, useCanEditSettings } from "../lib/settings";

const KINDS: KindEnum[] = ["included", "excluded", "condition", "guarantee"];

interface InstallmentRow {
  amount: string;
  step: string;
  label: Texts;
  needsConfirmation: boolean;
  note: string;
}
interface ItemRow {
  kind: KindEnum;
  text: Texts;
  needsConfirmation: boolean;
  note: string;
}

function fromPackage(pkg: ServicePackage | undefined) {
  return {
    code: pkg?.code ?? "",
    name: asTexts(pkg?.name),
    description: asTexts(pkg?.description),
    duration: asTexts(pkg?.duration),
    pricing: (pkg?.pricing ?? "fixed") as PricingEnum,
    price: pkg?.announced_price_xaf != null ? formatXaf(pkg.announced_price_xaf, { suffix: false }) : "",
    secondClient: pkg?.requires_second_client ?? false,
    needsConfirmation: pkg?.needs_confirmation ?? false,
    note: pkg?.confirmation_note ?? "",
    installments: (pkg?.installments ?? []).map<InstallmentRow>((i) => ({
      amount: i.amount_xaf != null ? formatXaf(i.amount_xaf, { suffix: false }) : "",
      step: i.trigger_step ?? "",
      label: asTexts(i.label),
      needsConfirmation: i.needs_confirmation ?? false,
      note: i.confirmation_note ?? "",
    })),
    items: (pkg?.items ?? []).map<ItemRow>((item) => ({
      kind: item.kind,
      text: asTexts(item.text),
      needsConfirmation: item.needs_confirmation ?? false,
      note: item.confirmation_note ?? "",
    })),
  };
}

type Form = ReturnType<typeof fromPackage>;

function Blockers({ blockers }: { blockers: readonly Record<string, unknown>[] }) {
  const { t, i18n } = useTranslation();
  if (!blockers.length) return null;
  return (
    <GlassCard as="section" role="alert" className="stack notice-warning">
      <h2>{t("catalog.blockersTitle")}</h2>
      <ul>
        {blockers.map((blocker, index) => {
          const params = {
            ...blocker,
            total: typeof blocker.total === "number" ? formatXaf(blocker.total) : "",
            announced: typeof blocker.announced === "number" ? formatXaf(blocker.announced) : "",
          };
          const note = typeof blocker.note === "string" && blocker.note ? ` — ${blocker.note}` : "";
          return (
            <li key={index} lang={note ? "fr" : i18n.language}>
              {t(`catalog.blockers.${String(blocker.code)}`, params)}
              {note}
            </li>
          );
        })}
      </ul>
    </GlassCard>
  );
}

function Versions({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const versions = useCatalogPackagesVersionsList(id);
  if (versions.data?.status !== 200 || !versions.data.data.length) return null;
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("catalog.versions")}</h2>
      <ul className="plain-list">
        {versions.data.data.map((version) => {
          const snapshot = version.snapshot as { price_xaf?: number | null };
          return (
            <li key={version.id}>
              {t("catalog.versionLine", {
                number: version.number,
                date: new Date(version.created_at).toLocaleDateString(locale, { timeZone: "Africa/Douala" }),
                price: snapshot.price_xaf != null ? formatXaf(snapshot.price_xaf) : "—",
              })}
            </li>
          );
        })}
      </ul>
    </GlassCard>
  );
}

function Editor({ pkg, service }: { pkg?: ServicePackage; service: Service }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = useCanEditSettings();
  const [form, setForm] = useState<Form>(() => fromPackage(pkg));
  const [invalidAmounts, setInvalidAmounts] = useState(false);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getCatalogPackagesListQueryKey() });
    if (pkg) void queryClient.invalidateQueries({ queryKey: getCatalogPackagesRetrieveQueryKey(pkg.id) });
  };
  const create = useCatalogPackagesCreate();
  const update = useCatalogPackagesPartialUpdate({ mutation: { onSuccess: refresh } });
  const publish = useCatalogPackagesPublishCreate({ mutation: { onSuccess: refresh } });
  const archive = useCatalogPackagesArchiveCreate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(create.error ?? update.error);
  const publishErrors = (publish.error as { body?: { blockers?: Record<string, unknown>[] } } | null)?.body
    ?.blockers;
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));
  const steps = service.steps ?? [];

  const amounts = form.installments.map((row) => readAmount(row.amount));
  const price = readAmount(form.price);
  const total = amounts.includes(undefined)
    ? null
    : installmentsTotal(amounts.map((amount) => ({ amount_xaf: amount ?? null })));
  const mismatch = form.pricing === "fixed" && total != null && price != null && price !== undefined && total !== price;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (amounts.includes(undefined) || price === undefined) {
      setInvalidAmounts(true);
      return;
    }
    setInvalidAmounts(false);
    publish.reset();
    const data: ServicePackageRequest = {
      service: service.id,
      code: form.code,
      name: form.name,
      description: form.description,
      duration: form.duration,
      pricing: form.pricing,
      announced_price_xaf: form.pricing === "fixed" ? price : null,
      requires_second_client: form.secondClient,
      needs_confirmation: form.needsConfirmation,
      confirmation_note: form.needsConfirmation ? form.note : "",
      installments: form.pricing === "fixed"
        ? form.installments.map((row, index) => ({
            number: index + 1,
            amount_xaf: amounts[index] ?? null,
            trigger_step_key: row.step || null,
            label: row.label,
            needs_confirmation: row.needsConfirmation,
            confirmation_note: row.needsConfirmation ? row.note : "",
          }))
        : [],
      items: form.items.map((row, index) => ({
        kind: row.kind,
        text: row.text,
        order: index + 1,
        needs_confirmation: row.needsConfirmation,
        confirmation_note: row.needsConfirmation ? row.note : "",
      })),
    };
    if (pkg) update.mutate({ id: pkg.id, data });
    else
      create.mutate(
        { data },
        {
          onSuccess: (response) => {
            refresh();
            if (response.status === 201) navigate(`/catalog/packages/${response.data.id}`, { replace: true });
          },
        },
      );
  };

  const setInstallment = (index: number, patch: Partial<InstallmentRow>) =>
    set("installments", form.installments.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const setItem = (index: number, patch: Partial<ItemRow>) =>
    set("items", form.items.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const saving = create.isPending || update.isPending;
  // The query is refreshed after each save or publication: it is the source of truth.
  const current = pkg ?? (create.data?.status === 201 ? create.data.data : undefined);

  return (
    <form onSubmit={onSubmit} className="editor-layout" noValidate>
      <GlassCard className="stack editor-head">
        <Link to="/catalog" className="link">
          ← {t("common.back")}
        </Link>
        <div className="row between">
          <h1>{pkg ? localized(pkg.name, i18n.language) : t("catalog.newPackage")}</h1>
          {current ? (
            <Badge tone={current.status === "published" ? "success" : "default"}>
              {t(`catalog.status.${current.status}`)}
              {current.version ? ` · v${current.version}` : ""}
            </Badge>
          ) : null}
        </div>
        <p className="muted">
          <span className="code-chip">{service.code}</span> {localized(service.name, i18n.language)}
        </p>
      </GlassCard>

      <div className="editor-layout__main">
      {current ? <Blockers blockers={(publishErrors ?? current.blockers) as Record<string, unknown>[]} /> : null}

      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <GlassCard as="section" className="stack">
          <h2 className="ao-card__title">{t("catalog.sections.identity")}</h2>
          <TextField
            label={t("catalog.fields.code")}
            value={form.code}
            hint={t("catalog.codeHint")}
            error={errors.code}
            onChange={(e) => set("code", e.target.value.toUpperCase())}
          />
          <TranslatedFields label={t("catalog.fields.name")} value={form.name} error={errors.name} onChange={(v) => set("name", v)} />
          <TranslatedFields
            label={t("catalog.fields.description")}
            value={form.description}
            multiline
            required={false}
            onChange={(v) => set("description", v)}
          />
          <TranslatedFields
            label={t("catalog.fields.duration")}
            value={form.duration}
            required={false}
            onChange={(v) => set("duration", v)}
          />
          <SoftToggle label={t("catalog.fields.secondClient")} checked={form.secondClient} onChange={(v) => set("secondClient", v)} />
          <SoftToggle label={t("catalog.needsConfirmation")} checked={form.needsConfirmation} onChange={(v) => set("needsConfirmation", v)} />
          {form.needsConfirmation ? (
            <TextField label={t("catalog.confirmationNote")} value={form.note} maxLength={300} onChange={(e) => set("note", e.target.value)} />
          ) : null}
        </GlassCard>

        <GlassCard as="section" className="stack">
          <h2 className="ao-card__title">{t("catalog.sections.pricing")}</h2>
          <SelectField
            label={t("catalog.fields.pricing")}
            value={form.pricing}
            options={(["fixed", "quote", "free"] as const).map((p) => ({ value: p, label: t(`catalog.pricing.${p}`) }))}
            onChange={(e) => set("pricing", e.target.value as PricingEnum)}
          />
          {form.pricing === "fixed" ? (
            <>
              <TextField
                label={t("catalog.fields.price")}
                inputMode="numeric"
                value={form.price}
                hint={t("catalog.priceHint")}
                error={errors.announced_price_xaf ?? (price === undefined && invalidAmounts ? t("common.fixErrors") : undefined)}
                onChange={(e) => set("price", e.target.value)}
              />
              <h2>{t("catalog.installments")}</h2>
              {form.installments.map((row, index) => (
                <div key={index} className="installment-row stack">
                  <div className="row between">
                    <h3>{t("catalog.installment", { number: index + 1 })}</h3>
                    <Button variant="ghost" onClick={() => set("installments", form.installments.filter((_, i) => i !== index))}>
                      {t("common.remove")}
                    </Button>
                  </div>
                  <TextField
                    label={t("catalog.fields.amount")}
                    inputMode="numeric"
                    value={row.amount}
                    error={amounts[index] === undefined && invalidAmounts ? t("common.fixErrors") : undefined}
                    onChange={(e) => setInstallment(index, { amount: e.target.value })}
                  />
                  <SelectField
                    label={t("catalog.fields.step")}
                    value={row.step}
                    options={[
                      { value: "", label: t("catalog.noStep") },
                      ...steps.map((s) => ({ value: s.key, label: localized(s.name, i18n.language) })),
                    ]}
                    onChange={(e) => setInstallment(index, { step: e.target.value })}
                  />
                  <TranslatedFields
                    label={t("catalog.fields.trigger")}
                    value={row.label}
                    required={false}
                    onChange={(v) => setInstallment(index, { label: v })}
                  />
                  <SoftToggle
                    label={t("catalog.needsConfirmation")}
                    checked={row.needsConfirmation}
                    onChange={(v) => setInstallment(index, { needsConfirmation: v })}
                  />
                  {row.needsConfirmation ? (
                    <TextField
                      label={t("catalog.confirmationNote")}
                      value={row.note}
                      maxLength={300}
                      onChange={(e) => setInstallment(index, { note: e.target.value })}
                    />
                  ) : null}
                </div>
              ))}
              {errors.installments ? <p role="alert" className="ao-field__error">{errors.installments}</p> : null}
              <div className="row">
                <Button
                  variant="secondary"
                  onClick={() =>
                    set("installments", [
                      ...form.installments,
                      { amount: "", step: "", label: {}, needsConfirmation: false, note: "" },
                    ])
                  }
                >
                  {t("catalog.addInstallment")}
                </Button>
              </div>
              <p role="status" className={mismatch ? "ko" : "muted"}>
                {total == null
                  ? ""
                  : mismatch && price != null
                    ? t("catalog.totalMismatch", { total: formatXaf(total), price: formatXaf(price) })
                    : t("catalog.total", { total: formatXaf(total) })}
              </p>
            </>
          ) : null}
        </GlassCard>

        {KINDS.map((kind) => (
          <GlassCard key={kind} as="section" className="stack">
            <h2>{t(`catalog.kind.${kind}`)}</h2>
            {form.items.map((row, index) =>
              row.kind !== kind ? null : (
                <div key={index} className="item-row stack">
                  <TranslatedFields label={t("catalog.fields.text")} value={row.text} onChange={(v) => setItem(index, { text: v })} />
                  <div className="row">
                    <SoftToggle
                      label={t("catalog.needsConfirmation")}
                      checked={row.needsConfirmation}
                      onChange={(v) => setItem(index, { needsConfirmation: v })}
                    />
                    <Button variant="ghost" onClick={() => set("items", form.items.filter((_, i) => i !== index))}>
                      {t("common.remove")}
                    </Button>
                  </div>
                  {row.needsConfirmation ? (
                    <TextField
                      label={t("catalog.confirmationNote")}
                      value={row.note}
                      maxLength={300}
                      onChange={(e) => setItem(index, { note: e.target.value })}
                    />
                  ) : null}
                </div>
              ),
            )}
            <div className="row">
              <Button
                variant="secondary"
                onClick={() => set("items", [...form.items, { kind, text: {}, needsConfirmation: false, note: "" }])}
              >
                {t("catalog.addItem")}
              </Button>
            </div>
          </GlassCard>
        ))}
      </fieldset>
      </div>

      <aside className="editor-layout__aside">
      <GlassCard as="section" className="stack editor-summary" aria-label={t("catalog.sections.summary")}>
        <span className="editor-summary__label">{t("catalog.fields.price")}</span>
        <span className="editor-summary__price ao-num">
          {form.pricing === "fixed" ? (price != null ? formatXaf(price) : "—") : t(`catalog.pricing.${form.pricing}`)}
        </span>
        {form.pricing === "fixed" ? (
          <ol className="editor-summary__plan" aria-label={t("catalog.installments")}>
            {form.installments.map((_row, index) => (
              <li key={index}>
                <span className="muted">{t("catalog.installment", { number: index + 1 })}</span>
                <span className="ao-num">{amounts[index] != null ? formatXaf(amounts[index] ?? 0) : "—"}</span>
              </li>
            ))}
          </ol>
        ) : null}
        <span className="muted editor-summary__items">
          {KINDS.map((kind) => `${form.items.filter((row) => row.kind === kind).length} ${t(`catalog.kind.${kind}`).toLowerCase()}`).join(" · ")}
        </span>
      </GlassCard>
      {canEdit ? (
        <GlassCard className="stack editor-actions">
          <div className="row">
            <Button type="submit" disabled={saving}>
              {saving ? t("common.loading") : t("catalog.save")}
            </Button>
            {current ? (
              <Button
                variant="secondary"
                disabled={publish.isPending || current.blockers.length > 0}
                onClick={() => publish.mutate({ id: current.id })}
              >
                {t("catalog.publish")}
              </Button>
            ) : null}
            {current && current.status !== "archived" && current.version > 0 ? (
              <Button variant="ghost" onClick={() => archive.mutate({ id: current.id })}>
                {t("catalog.archive")}
              </Button>
            ) : null}
            <p role="status" className={errors && Object.keys(errors).length ? "ko" : "ok"}>
              {Object.keys(errors).length
                ? t("common.fixErrors")
                : publish.data?.status === 200
                  ? t("catalog.published", { version: publish.data.data.version })
                  : update.isSuccess
                    ? t("common.saved")
                    : ""}
            </p>
          </div>
          <p className="muted">{t("catalog.publishHint")}</p>
        </GlassCard>
      ) : null}

      {pkg ? <Versions id={pkg.id} /> : null}
      </aside>
    </form>
  );
}

export function PackageEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === "new";
  const pkg = useCatalogPackagesRetrieve(id ?? "", { query: { enabled: !isNew } });
  const serviceId = isNew ? search.get("service") ?? "" : pkg.data?.status === 200 ? pkg.data.data.service : "";
  const service = useCatalogServicesRetrieve(serviceId, { query: { enabled: Boolean(serviceId) } });

  let content;
  if ((!isNew && pkg.isPending) || service.isPending)
    content = (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  else if (service.data?.status !== 200 || (!isNew && pkg.data?.status !== 200))
    content = <GlassCard role="alert">{t("common.error")}</GlassCard>;
  else
    content = (
      <Editor
        key={pkg.data?.status === 200 ? pkg.data.data.id : "new"}
        pkg={pkg.data?.status === 200 ? pkg.data.data : undefined}
        service={service.data.data}
      />
    );
  return <Page>{content}</Page>;
}
